import { useEffect, useState } from 'preact/hooks';
import { activeSkillStatuses } from '@/skill-status';
import { skillHelp } from '@/skill-help';
import { useClient } from '@/ui/context';
import { useSpellIconUrls } from '@/ui/in-game/use-spell-icon';
import { UI_PANEL_BG, UI_PANEL_BORDER, HUD_TEXT } from '@/ui/consts';

type ActiveBuff = ReturnType<typeof activeSkillStatuses>[number];

function BuffIcon({ id, active }: ActiveBuff) {
  const client = useClient();
  const record = client.getEsfRecordById(id);
  const name = record?.name || `Skill ${id}`;
  const icons = useSpellIconUrls(record?.iconId ?? null);
  const seconds = Math.ceil(active / 1000);
  const detail = skillHelp(id, name);
  const label = `${name} · ${seconds}s remaining${detail ? `. ${detail}` : ''}`;

  return (
    <li
      class={`relative flex shrink-0 items-center justify-center rounded border ${UI_PANEL_BORDER} ${UI_PANEL_BG} ${HUD_TEXT}`}
      style={{ width: '38px', height: '40px' }}
      title={label}
      aria-label={label}
      data-active-buff={id}
    >
      {icons ? (
        <img src={icons.active} alt='' width={34} height={32} draggable={false} />
      ) : (
        <span class='text-[10px]'>{name.split(' ').map(word => word[0]).join('').slice(0, 3)}</span>
      )}
      <span class='absolute right-0 bottom-0 rounded bg-black/85 px-1 text-[10px] leading-tight text-white'>
        {seconds}s
      </span>
    </li>
  );
}

export function ActiveBuffs() {
  const client = useClient();
  const [buffs, setBuffs] = useState(() => activeSkillStatuses(client));

  useEffect(() => {
    const update = () => setBuffs(activeSkillStatuses(client));
    update();
    const timer = window.setInterval(update, 250);
    return () => window.clearInterval(timer);
  }, [client]);

  if (!buffs.length) return null;

  return (
    <ul
      aria-label='Active effects'
      class='pointer-events-auto mb-1 flex list-none flex-wrap gap-1 p-0'
      style={{ maxWidth: 'min(320px, calc(100vw - 80px))' }}
      onPointerDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
      onContextMenu={event => event.stopPropagation()}
    >
      {buffs.map(buff => <BuffIcon key={buff.id} {...buff} />)}
    </ul>
  );
}
